"use client";

import { useEffect } from "react";

/**
 * Centres a pinned (sticky) stage in the window while it holds, without giving it a fixed
 * 100svh height. Sets `--pin-top` on the element matching `selector`; its CSS uses
 * `top: var(--pin-top, 0px)` and `height: auto`.
 *
 * WHY (2026-10-06). Pinned stages were exactly 100svh. When their content is shorter than the
 * window (short or narrow windows), the leftover became an empty band while pinned and a "fat
 * gap" before the next section once it unpinned (Tom, in Safari, on the homepage x-ray). A
 * content-height stage leaves nothing over when it unpins; this centres it while pinned.
 * Without JS the offset is 0 and the stage pins top-aligned, which still has no gap.
 * Check for the fault on any site with tom-design/templates/check-pinned-gaps.mjs.
 */
export function CentrePinned({ selector }: { selector: string }) {
  useEffect(() => {
    const el = document.querySelector<HTMLElement>(selector);
    if (!el || typeof ResizeObserver === "undefined") return;
    const apply = () => {
      el.style.setProperty("--pin-top", `max(0px, calc((100svh - ${el.offsetHeight}px) / 2))`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [selector]);
  return null;
}
